// src/components/admin/AdminLayout.js  — UPDATED with role-based nav
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Squares2X2Icon, CubeIcon, QrCodeIcon, ClipboardDocumentListIcon,
  ArrowRightOnRectangleIcon, Bars3Icon, XMarkIcon,
  ChartBarIcon, ExclamationTriangleIcon, CalendarDaysIcon,
  ShoppingBagIcon, UsersIcon, ShieldCheckIcon,
} from '@heroicons/react/24/outline';

// permission key must match ROLE_PERMISSIONS in admin.py
const ALL_NAV = [
  { label:'Dashboard',       icon:Squares2X2Icon,           route:'/admin/dashboard',        perm:'dashboard'        },
  { label:'Products',        icon:CubeIcon,                 route:'/admin/products',         perm:'products'         },
  { label:'Fridges',         icon:QrCodeIcon,               route:'/admin/fridges',          perm:'fridges'          },
  { label:'Assign Products', icon:ClipboardDocumentListIcon, route:'/admin/assign-products',  perm:'assign_products'  },
  { label:'Stock Monitor',   icon:ExclamationTriangleIcon,  route:'/admin/stock-monitoring', perm:'stock_monitor'    },
  { label:'Sales Report',    icon:ChartBarIcon,             route:'/admin/sales-report',     perm:'sales_report'     },
  { label:'Refill Schedule', icon:CalendarDaysIcon,         route:'/admin/refill-schedule',  perm:'refill_schedule'  },
  { label:'Orders',          icon:ShoppingBagIcon,          route:'/admin/orders',           perm:'orders'           },
  { label:'Customers',       icon:UsersIcon,                route:'/admin/customers',        perm:'customers'        },
  { label:'Staff Manager',   icon:ShieldCheckIcon,          route:'/admin/staff',            perm:'customers', adminOnly:true },
];

const AdminLayout = ({ children }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { admin, logout, hasPermission, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);

  const isActive = (r) => location.pathname === r;

  // Filter nav items based on role
  const visibleNav = ALL_NAV.filter(item => {
    if (item.adminOnly && !isAdmin) return false;
    return hasPermission(item.perm);
  });

  const roleColor  = isAdmin ? '#059669' : '#0891b2';
  const roleBg     = isAdmin ? '#ecfdf5'  : '#eff6ff';
  const roleBorder = isAdmin ? '#a7f3d0'  : '#bfdbfe';
  const roleLabel  = isAdmin ? 'Admin'    : 'Staff';

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *,*::before,*::after { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Nunito',sans-serif; background:#f1f5f9; color:#1e293b; }

    .ah { position:sticky; top:0; z-index:50; background:rgba(255,255,255,0.97);
          backdrop-filter:blur(14px); border-bottom:1px solid rgba(5,150,105,0.15);
          box-shadow:0 2px 16px rgba(0,0,0,0.06); }
    .ah-in { max-width:1400px; margin:0 auto; padding:0 0.75rem;
             display:flex; align-items:center; justify-content:space-between;
             height:52px; gap:0.5rem; }
    @media(min-width:480px)  { .ah-in { height:58px; padding:0 1rem; } }
    @media(min-width:768px)  { .ah-in { height:64px; padding:0 1.5rem; } }
    @media(min-width:1200px) { .ah-in { padding:0 2rem; } }

    .ah-logo { display:flex; align-items:center; gap:0.5rem; cursor:pointer; flex-shrink:0; }
    .ah-logo img { height:26px; width:auto; object-fit:contain; }
    @media(min-width:480px)  { .ah-logo img { height:30px; } }
    @media(min-width:768px)  { .ah-logo img { height:36px; } }
    .ah-logo-text { display:none; }
    @media(min-width:768px) {
      .ah-logo-text { display:block; }
      .ah-logo-name { font-size:1rem; font-weight:900;
                      background:linear-gradient(135deg,#059669,#0891b2);
                      -webkit-background-clip:text; -webkit-text-fill-color:transparent; line-height:1.1; }
      .ah-logo-sub  { font-size:0.6rem; color:#6b7280; font-weight:600; }
    }

    .ah-nav { display:none; }
    @media(min-width:900px) {
      .ah-nav { display:flex; align-items:center; gap:0.08rem; flex:1;
                margin:0 0.5rem; overflow-x:auto; scrollbar-width:none; }
      .ah-nav::-webkit-scrollbar { display:none; }
    }
    @media(min-width:1100px) { .ah-nav { gap:0.12rem; margin:0 0.85rem; } }
    @media(min-width:1280px) { .ah-nav { gap:0.18rem; margin:0 1.25rem; } }

    .ah-nb { display:flex; align-items:center; gap:0.25rem; padding:0.32rem 0.5rem;
             border-radius:9999px; font-weight:700; font-size:0.66rem; color:#475569;
             border:none; background:none; cursor:pointer; transition:all 0.2s;
             white-space:nowrap; font-family:'Nunito',sans-serif; flex-shrink:0; }
    @media(min-width:1000px) { .ah-nb { padding:0.35rem 0.62rem; font-size:0.7rem; } }
    @media(min-width:1100px) { .ah-nb { padding:0.38rem 0.72rem; font-size:0.73rem; } }
    @media(min-width:1280px) { .ah-nb { padding:0.4rem  0.82rem; font-size:0.77rem; } }
    .ah-nb svg { width:0.78rem; height:0.78rem; flex-shrink:0; }
    @media(min-width:1100px) { .ah-nb svg { width:0.85rem; height:0.85rem; } }
    .ah-nb:hover { background:#f0fdf4; color:#059669; }
    .ah-nb.on    { background:linear-gradient(135deg,#ecfdf5,#e0f2fe); color:#059669; border:1.5px solid #a7f3d0; }

    .ah-acts { display:flex; align-items:center; gap:0.35rem; flex-shrink:0; }
    @media(min-width:480px) { .ah-acts { gap:0.45rem; } }

    /* Role badge in header */
    .ah-role-chip { display:none; align-items:center; gap:0.38rem; border-radius:9999px;
                    padding:0.28rem 0.65rem 0.28rem 0.28rem; border:1px solid; }
    @media(min-width:1000px) { .ah-role-chip { display:flex; } }
    .ah-av { width:24px; height:24px; border-radius:50%;
             display:flex; align-items:center; justify-content:center;
             color:white; font-weight:800; font-size:0.62rem; flex-shrink:0; }
    .ah-uname { font-size:0.72rem; font-weight:700; }
    .ah-role-label { font-size:0.6rem; font-weight:800; padding:0.1rem 0.38rem;
                     border-radius:9999px; letter-spacing:0.3px; }

    .ah-logout { display:flex; align-items:center; gap:0.32rem; padding:0.32rem 0.65rem;
                 border-radius:9999px; background:#fff1f2; color:#e11d48;
                 border:1.5px solid #fecdd3; font-weight:700; font-size:0.7rem;
                 cursor:pointer; transition:all 0.2s; font-family:'Nunito',sans-serif; white-space:nowrap; }
    @media(min-width:480px) { .ah-logout { padding:0.36rem 0.8rem; font-size:0.75rem; } }
    @media(min-width:768px) { .ah-logout { padding:0.42rem 1rem;   font-size:0.8rem;  } }
    .ah-logout svg { width:0.85rem; height:0.85rem; }
    .ah-logout:hover { background:#e11d48; color:white; border-color:#e11d48; }

    .ah-ham { width:32px; height:32px; border-radius:0.45rem; display:flex;
              align-items:center; justify-content:center; background:#f8fafc;
              border:1px solid #e2e8f0; color:#475569; cursor:pointer; transition:all 0.2s; }
    @media(min-width:480px) { .ah-ham { width:36px; height:36px; } }
    @media(min-width:900px) { .ah-ham { display:none; } }
    .ah-ham:hover { background:#f0fdf4; color:#059669; border-color:#a7f3d0; }
    .ah-ham svg { width:1rem; height:1rem; }

    /* MOBILE DRAWER */
    .adw { position:fixed; inset:0; z-index:100; pointer-events:none; }
    .adw.open { pointer-events:all; }
    .adw-ov { position:absolute; inset:0; background:rgba(0,0,0,0); transition:background 0.3s; }
    .adw.open .adw-ov { background:rgba(0,0,0,0.42); }
    .adw-panel { position:absolute; top:0; left:0; width:min(285px,88vw); height:100%;
                 background:white; transform:translateX(-100%);
                 transition:transform 0.32s cubic-bezier(0.4,0,0.2,1);
                 display:flex; flex-direction:column; box-shadow:8px 0 30px rgba(0,0,0,0.12); }
    .adw.open .adw-panel { transform:translateX(0); }

    .adw-hdr { display:flex; align-items:center; justify-content:space-between;
               padding:0.85rem 1.1rem; border-bottom:1px solid #f3f4f6; gap:0.5rem; }
    .adw-logo { display:flex; align-items:center; gap:0.45rem; }
    .adw-logo img { height:26px; width:auto; object-fit:contain; }
    .adw-logo-name { font-size:0.9rem; font-weight:900;
                     background:linear-gradient(135deg,#059669,#0891b2);
                     -webkit-background-clip:text; -webkit-text-fill-color:transparent; }
    .adw-badge { color:white; font-size:0.6rem; font-weight:700; padding:0.16rem 0.52rem;
                 border-radius:9999px; flex-shrink:0; }
    .adw-close { width:26px; height:26px; border-radius:50%; background:#f8fafc;
                 border:1px solid #e2e8f0; color:#475569; cursor:pointer;
                 display:flex; align-items:center; justify-content:center; }
    .adw-close svg { width:0.85rem; height:0.85rem; }

    .adw-nav { flex:1; padding:0.4rem 0; overflow-y:auto; }
    .adw-section { font-size:0.62rem; font-weight:800; color:#9ca3af;
                   text-transform:uppercase; letter-spacing:0.6px;
                   padding:0.7rem 1.25rem 0.25rem; }
    .adw-link { display:flex; align-items:center; gap:0.62rem; padding:0.72rem 1.25rem;
                font-size:0.88rem; font-weight:700; color:#374151; border:none; background:none;
                cursor:pointer; border-left:3px solid transparent; transition:all 0.2s;
                width:100%; font-family:'Nunito',sans-serif; text-align:left; }
    .adw-link svg { width:1.05rem; height:1.05rem; flex-shrink:0; }
    .adw-link:hover { background:#f0fdf4; color:#059669; border-left-color:#059669; }
    .adw-link.on    { background:#ecfdf5; color:#059669; border-left-color:#059669; font-weight:800; }

    /* Locked item in drawer */
    .adw-link-locked { display:flex; align-items:center; gap:0.62rem; padding:0.72rem 1.25rem;
                       font-size:0.88rem; font-weight:700; color:#cbd5e1;
                       border-left:3px solid transparent; width:100%; cursor:not-allowed; }
    .adw-link-locked svg { width:1.05rem; height:1.05rem; flex-shrink:0; }
    .adw-lock-icon { width:0.75rem!important; height:0.75rem!important; margin-left:auto; opacity:0.5; }

    .adw-ftr { padding:0.85rem 1.25rem; border-top:1px solid #f3f4f6; }
    .adw-user { font-size:0.74rem; color:#6b7280; margin-bottom:0.6rem; font-weight:600; }
    .adw-logout { width:100%; display:flex; align-items:center; justify-content:center;
                  gap:0.42rem; padding:0.65rem 1rem; border-radius:0.7rem;
                  background:#fff1f2; color:#e11d48; border:1.5px solid #fecdd3;
                  font-weight:700; font-size:0.85rem; cursor:pointer; transition:all 0.2s; font-family:'Nunito',sans-serif; }
    .adw-logout:hover { background:#e11d48; color:white; }
    .adw-logout svg { width:0.95rem; height:0.95rem; }

    /* Staff access-denied banner */
    .staff-banner { background:linear-gradient(135deg,#eff6ff,#f0f9ff); border:1px solid #bfdbfe;
                    border-radius:0.875rem; padding:0.65rem 1.1rem; margin-bottom:1.25rem;
                    display:flex; align-items:center; gap:0.65rem; font-size:0.82rem; font-weight:700; color:#1d4ed8; }
    .staff-banner svg { width:1rem; height:1rem; flex-shrink:0; }

    /* PAGE */
    .adm-page { min-height:calc(100vh - 52px); padding:1rem 0.75rem; }
    @media(min-width:480px)  { .adm-page { min-height:calc(100vh - 58px); padding:1.25rem 1rem; } }
    @media(min-width:768px)  { .adm-page { min-height:calc(100vh - 64px); padding:1.75rem 1.5rem; } }
    @media(min-width:1024px) { .adm-page { padding:2rem 2rem; } }
    .adm-content { max-width:1400px; margin:0 auto; }
  `;

  // Drawer nav: show all items, lock restricted ones for staff
  const drawerNav = ALL_NAV.filter(item => !(item.adminOnly && !isAdmin));

  return (
    <>
      <style>{css}</style>

      {/* ── HEADER ── */}
      <header className="ah">
        <div className="ah-in">
          <div className="ah-logo" onClick={() => navigate('/admin/dashboard')}>
            <img src="/images/logo.png" alt="Fresh Hills"
              onError={e => { e.target.onerror=null; e.target.style.display='none'; }} />
            <div className="ah-logo-text">
              <div className="ah-logo-name">Fresh Hills</div>
              <div className="ah-logo-sub">Admin Panel</div>
            </div>
          </div>

          {/* Desktop nav — only permitted items */}
          <nav className="ah-nav">
            {visibleNav.map(item => (
              <button key={item.route}
                className={`ah-nb${isActive(item.route)?' on':''}`}
                onClick={() => navigate(item.route)}>
                <item.icon />{item.label}
              </button>
            ))}
          </nav>

          <div className="ah-acts">
            {/* Role chip */}
            <div className="ah-role-chip"
              style={{ background: roleBg, borderColor: roleBorder }}>
              <div className="ah-av"
                style={{ background: `linear-gradient(135deg, ${roleColor}, #0891b2)` }}>
                {admin?.username?.[0]?.toUpperCase() || 'U'}
              </div>
              <span className="ah-uname" style={{ color: '#374151' }}>
                {admin?.username || 'User'}
              </span>
              <span className="ah-role-label"
                style={{ background: roleColor, color: 'white' }}>
                {roleLabel}
              </span>
            </div>

            <button className="ah-logout" onClick={logout}>
              <ArrowRightOnRectangleIcon /><span>Logout</span>
            </button>
            <button className="ah-ham" onClick={() => setOpen(true)}>
              <Bars3Icon />
            </button>
          </div>
        </div>
      </header>

      {/* ── MOBILE DRAWER ── */}
      <div className={`adw${open?' open':''}`}>
        <div className="adw-ov" onClick={() => setOpen(false)} />
        <div className="adw-panel">
          <div className="adw-hdr">
            <div className="adw-logo">
              <img src="/images/logo.png" alt="Fresh Hills"
                onError={e => { e.target.onerror=null; e.target.style.display='none'; }} />
              <span className="adw-logo-name">Fresh Hills</span>
            </div>
            <span className="adw-badge" style={{ background: roleColor }}>
              {roleLabel}
            </span>
            <button className="adw-close" onClick={() => setOpen(false)}><XMarkIcon /></button>
          </div>

          <nav className="adw-nav">
            <div className="adw-section">Navigation</div>
            {drawerNav.map(item => {
              const allowed = hasPermission(item.perm);
              if (!allowed) return (
                <div key={item.route} className="adw-link-locked">
                  <item.icon />
                  {item.label}
                  <span style={{ marginLeft:'auto', fontSize:'0.62rem', color:'#94a3b8', fontWeight:700 }}>
                    🔒 Admin only
                  </span>
                </div>
              );
              return (
                <button key={item.route}
                  className={`adw-link${isActive(item.route)?' on':''}`}
                  onClick={() => { navigate(item.route); setOpen(false); }}>
                  <item.icon />{item.label}
                </button>
              );
            })}
          </nav>

          <div className="adw-ftr">
            <div className="adw-user">
              Logged in as <strong>{admin?.username}</strong>
              {' '}·{' '}
              <span style={{ color: roleColor, fontWeight:800 }}>{roleLabel}</span>
            </div>
            <button className="adw-logout" onClick={logout}>
              <ArrowRightOnRectangleIcon /> Logout
            </button>
          </div>
        </div>
      </div>

      {/* ── CONTENT ── */}
      <main className="adm-page">
        <div className="adm-content">
          {/* Staff info banner */}
          {!isAdmin && (
            <div className="staff-banner">
              <ShieldCheckIcon />
              You are logged in as <strong style={{marginLeft:'0.2rem',marginRight:'0.2rem'}}>{admin?.username}</strong> (Staff).
              Some features are restricted to Admin only.
            </div>
          )}
          {children}
        </div>
      </main>
    </>
  );
};

export default AdminLayout;